import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCashRegister,
  FaChartLine,
  FaCheckCircle,
  FaClipboardList,
  FaCreditCard,
  FaEnvelope,
  FaFileInvoiceDollar,
  FaGlobe,
  FaLink,
  FaMobileAlt,
  FaPhoneAlt,
  FaStore,
  FaWallet,
  FaWhatsapp,
} from 'react-icons/fa';

const WHATSAPP_MESSAGE = encodeURIComponent('Olá Ecosistemas, gostaria de saber mais dos seus produtos.');
const WHATSAPP_URL = `https://wa.me/5567984485377?text=${WHATSAPP_MESSAGE}`;

const modules = [
  {
    icon: FaCalendarAlt,
    title: 'Controle de agenda',
    description:
      'Acompanhe os horários por smartphone, tablet ou notebook, com visão detalhada da agenda e dos dados de cada cliente.',
    bullets: ['Agenda detalhada', 'Acesso em qualquer dispositivo', 'Confirmações e cancelamentos automáticos'],
  },
  {
    icon: FaLink,
    title: 'Link de agendamento',
    description:
      'O cliente visualiza a disponibilidade em tempo real e agenda sozinho com qualquer profissional ou com um profissional específico.',
    bullets: ['Disponibilidade em tempo real', 'Reserva sem atrito', 'Agendamento direto pelo cliente'],
  },
  {
    icon: FaClipboardList,
    title: 'Gestão de agendamentos',
    description:
      'Centralize reservas, comandas, comissões e histórico em um painel web com agenda, caixa, fluxo financeiro e relatórios operacionais.',
    bullets: ['Histórico de serviços', 'Reservas e comissões em tempo real', 'Abertura e fechamento de caixa'],
  },
  {
    icon: FaCashRegister,
    title: 'Gestão de comandas',
    description:
      'Cada cliente tem uma comanda virtual para lançar produtos e serviços durante o atendimento, integrada ao financeiro e ao fechamento do caixa.',
    bullets: ['Comanda virtual por cliente', 'Lançamento rápido', 'Integração com caixa'],
  },
  {
    icon: FaWallet,
    title: 'Gestão financeira',
    description:
      'Controle contas a pagar e receber, lançamentos recorrentes, movimentações bancárias, plano de contas, centro de custo e o caixa diário.',
    bullets: ['Contas a pagar e receber', 'Movimentações bancárias', 'Centros de custo e plano de contas'],
  },
  {
    icon: FaFileInvoiceDollar,
    title: 'Gestão fiscal',
    description:
      'Emita NFe, NFCe e NFSe, faça carta de correção, entrada por XML e conduza a rotina fiscal com mais automação.',
    bullets: ['NFe e NFCe', 'NFSe via portal nacional', 'Entrada XML e carta de correção'],
  },
  {
    icon: FaWhatsapp,
    title: 'Histórico de clientes',
    description:
      'Consulte o histórico de atendimento e automatize mensagens personalizadas no WhatsApp para relacionamento contínuo.',
    bullets: ['Histórico completo', 'Comunicação personalizada', 'Automação de mensagens'],
  },
  {
    icon: FaChartLine,
    title: 'Gestão inteligente',
    description:
      'Visualize gráficos, arquivos simples, relatórios personalizados e dashboards para decidir com mais velocidade.',
    bullets: ['Relatórios customizados', 'Dashboards operacionais', 'Leitura rápida dos resultados'],
  },
  {
    icon: FaCreditCard,
    title: 'Checkout Banco Asaas',
    description:
      'Receba já no momento do agendamento, garantindo cobrança imediata e mais previsibilidade no caixa.',
    bullets: ['Pagamento no agendamento', 'Cobrança imediata', 'Menos faltas e mais previsibilidade'],
  },
];

const integratedApps = [
  {
    icon: FaStore,
    title: 'SEUPOS',
    description:
      'PDV para Android, iOS e POS, funcionando online ou offline para dar autonomia em vendas, controle de caixa e emissão de NFC-e direto no app.',
    bullets: ['Android, iOS e POS', 'Operação online e offline', 'Adquirentes: Mercado Pago, Rede, Stone, Bin, Caixa e Sicredi'],
  },
  {
    icon: FaCreditCard,
    title: 'SEUPOS HUB',
    description:
      'Centraliza pagamentos, conecta o PDV direto aos adquirentes e automatiza cobrança com retorno financeiro integrado.',
    bullets: ['Centralização de pagamentos', 'Integração com BIN, Rede, Caixa e Sicredi', 'Automação do retorno financeiro'],
  },
  {
    icon: FaMobileAlt,
    title: 'CONNECT',
    description:
      'Substitui a complexidade do TEF tradicional por um fluxo de pagamento mais prático, rápido e eficiente.',
    bullets: ['Alternativa ao TEF tradicional', 'Menos custo operacional', 'Fluxo de pagamento mais ágil'],
  },
];

const heroMetrics = [
  '+30 anos de experiência em software',
  'Agenda, financeiro, fiscal e checkout em uma base só',
  'Operação pensada para negócios com alta recorrência',
];

const reveal = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: 94px;
  background:
    radial-gradient(circle at left 30%, rgba(126, 196, 232, 0.14) 0%, transparent 26%),
    radial-gradient(circle at right top, rgba(59, 158, 212, 0.18) 0%, transparent 28%),
    linear-gradient(180deg, #f6fbff 0%, #ffffff 58%, #f9fcff 100%);
  color: #173042;

  @media (max-width: 768px) {
    padding-top: 78px;
  }
`;

const Section = styled.section`
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
`;

const HeroSection = styled(Section)`
  padding: 54px 0 44px;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(340px, 0.9fr);
  gap: 48px;
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const BackButton = styled(motion.button)`
  border: none;
  background: none;
  padding: 0;
  margin-bottom: 28px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #2178ab;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
`;

const EyebrowGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 9px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid rgba(59, 158, 212, 0.14);
  color: #45667f;
  font-size: 0.85rem;
  font-weight: 700;
  box-shadow: 0 12px 24px rgba(59, 158, 212, 0.08);
`;

const Title = styled.h1`
  margin: 0 0 18px;
  font-size: clamp(3rem, 5.8vw, 5rem);
  line-height: 0.98;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #173042;
  max-width: 9ch;
`;

const TitleAccent = styled.span`
  color: #3b9ed4;
`;

const Lead = styled.p`
  margin: 0 0 18px;
  max-width: 640px;
  color: #4b6882;
  font-size: 1.12rem;
  line-height: 1.8;
`;

const SupportText = styled.p`
  margin: 0 0 28px;
  max-width: 640px;
  color: #30506a;
  font-size: 1rem;
  line-height: 1.75;
  font-weight: 600;
`;

const MetricRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 30px;
`;

const MetricPill = styled.div`
  padding: 12px 15px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(59, 158, 212, 0.14);
  color: #30506a;
  font-size: 0.93rem;
  font-weight: 700;
  box-shadow: 0 16px 30px rgba(59, 158, 212, 0.08);
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  padding: 0 24px;
  border-radius: 999px;
  background: linear-gradient(135deg, #3b9ed4 0%, #6bbddf 100%);
  color: #ffffff;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 18px 34px rgba(59, 158, 212, 0.22);
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 40px rgba(59, 158, 212, 0.26);
    color: #ffffff;
    text-decoration: none;
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  padding: 0 24px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(59, 158, 212, 0.16);
  color: #2178ab;
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(59, 158, 212, 0.28);
    box-shadow: 0 16px 30px rgba(59, 158, 212, 0.12);
    color: #2178ab;
    text-decoration: none;
  }
`;

const HeroCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(59, 158, 212, 0.14);
  border-radius: 34px;
  padding: 26px;
  box-shadow: 0 28px 60px rgba(59, 158, 212, 0.14);
  backdrop-filter: blur(18px);
`;

const HeroTopRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 16px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const MiniCard = styled.div`
  padding: 16px;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f4fbff 100%);
  border: 1px solid rgba(59, 158, 212, 0.12);
`;

const MiniLabel = styled.p`
  margin: 0 0 8px;
  color: #6a8899;
  font-size: 0.82rem;
  font-weight: 700;
`;

const MiniValue = styled.p`
  margin: 0;
  color: #173042;
  font-size: 1.35rem;
  font-weight: 900;
  line-height: 1.2;
`;

const DashboardCard = styled.div`
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(180deg, #eaf7ff 0%, #dff2fd 100%);
  border: 1px solid rgba(59, 158, 212, 0.18);
`;

const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const DashboardTitle = styled.h3`
  margin: 0;
  color: #173042;
  font-size: 1rem;
  font-weight: 800;
`;

const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #3b9ed4;
  box-shadow: 0 0 0 8px rgba(59, 158, 212, 0.14);
`;

const ScheduleList = styled.div`
  display: grid;
  gap: 12px;
`;

const ScheduleItem = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.8);
`;

const ScheduleTime = styled.div`
  color: #2178ab;
  font-weight: 800;
  font-size: 0.95rem;
`;

const ScheduleText = styled.div`
  display: grid;
  gap: 4px;
`;

const ScheduleTitle = styled.div`
  color: #173042;
  font-size: 0.95rem;
  font-weight: 800;
`;

const ScheduleMeta = styled.div`
  color: #567087;
  font-size: 0.84rem;
`;

const ContentSection = styled(Section)`
  padding: 44px 0 28px;
`;

const SectionHeader = styled.div`
  max-width: 760px;
  margin: 0 auto 34px;
  text-align: center;
`;

const EyebrowTag = styled.span`
  display: inline-flex;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(59, 158, 212, 0.08);
  color: #2178ab;
  font-size: 0.84rem;
  font-weight: 800;
  margin-bottom: 14px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 14px;
  color: #173042;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: -0.03em;
`;

const SectionLead = styled.p`
  margin: 0;
  color: #5a758d;
  font-size: 1.02rem;
  line-height: 1.8;
`;

const IntroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 24px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const IntroCard = styled.div`
  padding: 30px;
  border-radius: 28px;
  background: linear-gradient(180deg, #ffffff 0%, #f4fbff 100%);
  border: 1px solid rgba(59, 158, 212, 0.12);
  box-shadow: 0 16px 34px rgba(59, 158, 212, 0.08);
`;

const IntroTitle = styled.h3`
  margin: 0 0 12px;
  color: #173042;
  font-size: 1.5rem;
  font-weight: 800;
`;

const IntroText = styled.p`
  margin: 0;
  color: #567087;
  line-height: 1.85;
  font-size: 1rem;
`;

const QuickList = styled.div`
  display: grid;
  gap: 14px;
`;

const QuickItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(59, 158, 212, 0.12);
  color: #30506a;
  font-weight: 700;
  line-height: 1.6;
`;

const ModuleGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ModuleCard = styled(motion.article)`
  padding: 28px;
  border-radius: 28px;
  background: linear-gradient(180deg, #ffffff 0%, #f7fcff 100%);
  border: 1px solid rgba(59, 158, 212, 0.12);
  box-shadow: 0 16px 34px rgba(59, 158, 212, 0.08);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(59, 158, 212, 0.22);
    box-shadow: 0 24px 44px rgba(59, 158, 212, 0.12);
  }
`;

const ModuleIcon = styled.div`
  width: 58px;
  height: 58px;
  margin-bottom: 18px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(59, 158, 212, 0.14) 0%, rgba(107, 189, 223, 0.2) 100%);
  color: #2178ab;
  box-shadow: 0 14px 26px rgba(59, 158, 212, 0.12);
`;

const ModuleTitle = styled.h3`
  margin: 0 0 12px;
  color: #173042;
  font-size: 1.24rem;
  font-weight: 800;
`;

const ModuleText = styled.p`
  margin: 0 0 16px;
  color: #567087;
  font-size: 0.97rem;
  line-height: 1.8;
`;

const BulletList = styled.div`
  display: grid;
  gap: 10px;
`;

const Bullet = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #30506a;
  font-size: 0.92rem;
  line-height: 1.6;
  font-weight: 700;
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const AppCard = styled.div`
  padding: 28px;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(23, 48, 66, 0.96) 0%, rgba(33, 120, 171, 0.94) 100%);
  border: 1px solid rgba(107, 189, 223, 0.2);
  box-shadow: 0 24px 48px rgba(23, 48, 66, 0.18);
  color: #ffffff;
`;

const AppHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
`;

const AppIcon = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
`;

const AppTitle = styled.h3`
  margin: 0;
  font-size: 1.22rem;
  font-weight: 800;
`;

const AppText = styled.p`
  margin: 0 0 16px;
  color: rgba(255, 255, 255, 0.84);
  line-height: 1.75;
  font-size: 0.97rem;
`;

const AppBullet = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  color: #ffffff;
  font-size: 0.92rem;
  line-height: 1.6;
  font-weight: 700;
`;

const ContactCard = styled.div`
  padding: 34px;
  border-radius: 30px;
  background: linear-gradient(135deg, #f1f9ff 0%, #ffffff 100%);
  border: 1px solid rgba(59, 158, 212, 0.14);
  box-shadow: 0 18px 36px rgba(59, 158, 212, 0.08);
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-top: 26px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(59, 158, 212, 0.12);
  color: #173042;
  text-decoration: none;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(59, 158, 212, 0.22);
    box-shadow: 0 20px 34px rgba(59, 158, 212, 0.1);
    color: #173042;
    text-decoration: none;
  }
`;

const ContactIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 158, 212, 0.12);
  color: #2178ab;
  flex-shrink: 0;
`;

const ContactLabel = styled.div`
  color: #6a8899;
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 3px;
`;

const ContactValue = styled.div`
  color: #173042;
  font-size: 1rem;
  font-weight: 800;
  word-break: break-word;
`;

function EcosisAgenda() {
  const history = useHistory();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <HeroSection>
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <BackButton variants={reveal} whileHover={{ x: -6 }} onClick={() => history.push('/')}>
            <FaArrowLeft /> Voltar
          </BackButton>

          <HeroGrid>
            <motion.div variants={reveal}>
              <EyebrowGroup>
                <Eyebrow>Sistema Agenda</Eyebrow>
                <Eyebrow>Mais que agendamento</Eyebrow>
                <Eyebrow>Crescimento para o negócio</Eyebrow>
              </EyebrowGroup>

              <Title>
                <TitleAccent>Agenda</TitleAccent> que organiza o fluxo real do seu atendimento.
              </Title>

              <Lead>
                Feito para negócios com agenda cheia, movimento intenso e necessidade de operar com rapidez,
                previsibilidade e controle.
              </Lead>

              <SupportText>
                O ECOSIS Agenda permite agendamento online, controle de comissões, fluxo financeiro, automações
                e emissão fiscal em uma gestão pontual do começo ao fim.
              </SupportText>

              <MetricRow>
                {heroMetrics.map((metric) => (
                  <MetricPill key={metric}>{metric}</MetricPill>
                ))}
              </MetricRow>

              <CTAGroup>
                <PrimaryButton href="#modulos">Ver funcionalidades</PrimaryButton>
                <SecondaryButton href="#contato-agenda">Falar com a equipe</SecondaryButton>
              </CTAGroup>
            </motion.div>

            <HeroCard variants={reveal}>
              <HeroTopRow>
                <MiniCard>
                  <MiniLabel>Confirmações automáticas</MiniLabel>
                  <MiniValue>Agenda viva</MiniValue>
                </MiniCard>
                <MiniCard>
                  <MiniLabel>Financeiro e fiscal</MiniLabel>
                  <MiniValue>Tudo integrado</MiniValue>
                </MiniCard>
              </HeroTopRow>

              <DashboardCard>
                <DashboardHeader>
                  <DashboardTitle>Painel operacional</DashboardTitle>
                  <StatusDot />
                </DashboardHeader>

                <ScheduleList>
                  <ScheduleItem>
                    <ScheduleTime>08:30</ScheduleTime>
                    <ScheduleText>
                      <ScheduleTitle>Atendimento confirmado</ScheduleTitle>
                      <ScheduleMeta>Cliente notificou presença e pagamento vinculado ao agendamento</ScheduleMeta>
                    </ScheduleText>
                  </ScheduleItem>
                  <ScheduleItem>
                    <ScheduleTime>11:00</ScheduleTime>
                    <ScheduleText>
                      <ScheduleTitle>Comanda em andamento</ScheduleTitle>
                      <ScheduleMeta>Produtos e serviços lançados em tempo real com comissão automática</ScheduleMeta>
                    </ScheduleText>
                  </ScheduleItem>
                  <ScheduleItem>
                    <ScheduleTime>17:45</ScheduleTime>
                    <ScheduleText>
                      <ScheduleTitle>Fechamento de caixa</ScheduleTitle>
                      <ScheduleMeta>Resumo diário integrado ao financeiro, fiscal e dashboards</ScheduleMeta>
                    </ScheduleText>
                  </ScheduleItem>
                </ScheduleList>
              </DashboardCard>
            </HeroCard>
          </HeroGrid>
        </motion.div>
      </HeroSection>

      <ContentSection>
        <SectionHeader>
          <EyebrowTag>Feito para o sucesso do seu negócio</EyebrowTag>
          <SectionTitle>Mais que marcar horários: organizar atendimento, caixa e relacionamento.</SectionTitle>
          <SectionLead>
            Com agendas cheias e várias tarefas acontecendo ao mesmo tempo, o sistema foi pensado para reduzir atrito
            operacional e transformar o agendamento em um fluxo completo de gestão.
          </SectionLead>
        </SectionHeader>

        <IntroGrid>
          <IntroCard>
            <IntroTitle>Um software para uma gestão pontual.</IntroTitle>
            <IntroText>
              O material do ECOSIS Agenda posiciona a solução como uma base única para o dia a dia do negócio:
              agendamento online do cliente, confirmação automática, controle de atendimento, comissões, financeiro,
              fiscal e dashboards em uma só operação.
            </IntroText>
          </IntroCard>

          <QuickList>
            <QuickItem>
              <FaCheckCircle color="#3B9ED4" size={18} />
              Acesso por smartphone, tablet e notebook para acompanhar a agenda de qualquer lugar.
            </QuickItem>
            <QuickItem>
              <FaCheckCircle color="#3B9ED4" size={18} />
              Automação para confirmações, cancelamentos, reservas e comunicação contínua com o cliente.
            </QuickItem>
            <QuickItem>
              <FaCheckCircle color="#3B9ED4" size={18} />
              Fluxo completo com abertura e fechamento de caixa, cobrança no agendamento e gestão fiscal integrada.
            </QuickItem>
          </QuickList>
        </IntroGrid>
      </ContentSection>

      <ContentSection id="modulos">
        <SectionHeader>
          <EyebrowTag>Módulos principais</EyebrowTag>
          <SectionTitle>Todas as frentes operacionais do Agenda reunidas na mesma plataforma.</SectionTitle>
          <SectionLead>
            Cada módulo abaixo foi estruturado com as informações do material para cobrir a rotina de atendimento,
            cobrança, gestão e acompanhamento do cliente.
          </SectionLead>
        </SectionHeader>

        <ModuleGrid initial="hidden" animate="visible" variants={stagger}>
          {modules.map((module) => {
            const Icon = module.icon;

            return (
              <ModuleCard key={module.title} variants={reveal}>
                <ModuleIcon>
                  <Icon size={24} />
                </ModuleIcon>
                <ModuleTitle>{module.title}</ModuleTitle>
                <ModuleText>{module.description}</ModuleText>
                <BulletList>
                  {module.bullets.map((bullet) => (
                    <Bullet key={bullet}>
                      <FaCheckCircle color="#3B9ED4" size={14} style={{ flexShrink: 0, marginTop: 3 }} />
                      <span>{bullet}</span>
                    </Bullet>
                  ))}
                </BulletList>
              </ModuleCard>
            );
          })}
        </ModuleGrid>
      </ContentSection>

      <ContentSection>
        <SectionHeader>
          <EyebrowTag>Aplicativos integrados</EyebrowTag>
          <SectionTitle>Agenda conectado ao PDV e aos meios de pagamento.</SectionTitle>
          <SectionLead>
            O PDF também apresenta o ecossistema complementar para frente de caixa, adquirência e pagamentos,
            ampliando o uso do sistema além do agendamento.
          </SectionLead>
        </SectionHeader>

        <AppsGrid>
          {integratedApps.map((app) => {
            const Icon = app.icon;

            return (
              <AppCard key={app.title}>
                <AppHeader>
                  <AppIcon>
                    <Icon size={24} />
                  </AppIcon>
                  <AppTitle>{app.title}</AppTitle>
                </AppHeader>
                <AppText>{app.description}</AppText>
                <BulletList>
                  {app.bullets.map((bullet) => (
                    <AppBullet key={bullet}>
                      <FaCheckCircle size={14} style={{ flexShrink: 0, marginTop: 3 }} />
                      <span>{bullet}</span>
                    </AppBullet>
                  ))}
                </BulletList>
              </AppCard>
            );
          })}
        </AppsGrid>
      </ContentSection>

      <ContentSection id="contato-agenda">
        <ContactCard>
          <SectionHeader style={{ marginBottom: 0 }}>
            <EyebrowTag>Contato</EyebrowTag>
            <SectionTitle>Quer liberar o ECOSIS Agenda para sua operação?</SectionTitle>
            <SectionLead>
              Estes são os contatos informados no material: atendimento comercial, WhatsApp e site institucional.
            </SectionLead>
          </SectionHeader>

          <ContactGrid>
            <ContactItem href="mailto:ecosistemas.computacao@gmail.com">
              <ContactIcon>
                <FaEnvelope size={20} />
              </ContactIcon>
              <div>
                <ContactLabel>E-mail</ContactLabel>
                <ContactValue>ecosistemas.computacao@gmail.com</ContactValue>
              </div>
            </ContactItem>

            <ContactItem href={WHATSAPP_URL} target="_blank" rel="noreferrer">
              <ContactIcon>
                <FaPhoneAlt size={18} />
              </ContactIcon>
              <div>
                <ContactLabel>WhatsApp</ContactLabel>
                <ContactValue>(67) 98448-5377</ContactValue>
              </div>
            </ContactItem>

            <ContactItem href="https://www.ecosistemascomputacao.com.br" target="_blank" rel="noreferrer">
              <ContactIcon>
                <FaGlobe size={18} />
              </ContactIcon>
              <div>
                <ContactLabel>Site</ContactLabel>
                <ContactValue>www.ecosistemascomputacao.com.br</ContactValue>
              </div>
            </ContactItem>
          </ContactGrid>
        </ContactCard>
      </ContentSection>
    </PageContainer>
  );
}

export default EcosisAgenda;
